/** Minutes a time zone is ahead of UTC at `at`, read as the browser names its offset (`GMT+5:30`); null for a zone it does not know. */
export function offsetFromUtc(timeZone: string, at: Date): number | null {
    try {
        const name = new Intl.DateTimeFormat("en", { timeZone: timeZone, timeZoneName: "shortOffset" }).formatToParts(at).find((part) => part.type === "timeZoneName")?.value ?? "";
        const match = /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(name);
        if (!match) return null;
        if (!match[1]) return 0;
        return (match[1] === "-" ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3] ?? 0));
    } catch {
        return null;
    }
}

/** Minutes a time zone is ahead of the browser's at `at`, which settles summer time on both sides; null for an unknown zone. */
export function offsetFromBrowser(timeZone: string, at: Date): number | null {
    const offset = offsetFromUtc(timeZone, at);
    return offset === null ? null : offset + at.getTimezoneOffset();
}

/** `UTC+02:00`, `UTC-03:30`, `UTC` for the minutes a time zone is ahead of UTC. */
export function offsetText(minutes: number): string {
    if (minutes === 0) return "UTC";
    const hours = Math.floor(Math.abs(minutes) / 60);
    const rest = Math.abs(minutes) % 60;
    return `UTC${minutes < 0 ? "-" : "+"}${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/** The day and time (`YYYY-MM-DD`, `HH:mm:ss`) the clocks of a time zone read at an instant. */
export function wallOf(instant: number, timeZone: string): { day: string; time: string } {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(new Date(instant));
    const part = (type: string) => parts.find((candidate) => candidate.type === type)?.value ?? "00";
    return { day: `${part("year")}-${part("month")}-${part("day")}`, time: `${part("hour")}:${part("minute")}:${part("second")}` };
}

/**
 * The instant at which the clocks of a time zone read a day and a time, in milliseconds. A time the clocks read
 * twice, when summer time ends, is the first of the two; one they skip, when it starts, is read with the offset
 * before the change. Null for a zone the browser does not know.
 */
export function instantOfWall(day: string, time: string, timeZone: string): number | null {
    const [year, month, date] = day.slice(0, 10).split("-").map(Number);
    const [hour = 0, minute = 0, second = 0] = time.split(":").map(Number);
    const wall = Date.UTC(year, month - 1, date, hour, minute, second);
    const before = offsetFromUtc(timeZone, new Date(wall - 86400000));
    const after = offsetFromUtc(timeZone, new Date(wall + 86400000));
    if (before === null || after === null) return null;
    const valid = [...new Set([before, after])]
        .map((offset) => wall - offset * 60000)
        .filter((candidate) => offsetFromUtc(timeZone, new Date(candidate)) === (wall - candidate) / 60000);
    return valid.length ? Math.min(...valid) : wall - before * 60000;
}
